import React, { createContext, useContext, useState, useEffect } from "react";
import { authService, tokenStorage } from "../services/authService";

export interface UserProfile {
  id: number | string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  roles?: string[];
  permissions?: string[];
  tenant?: string;
  avatar?: string;
  must_change_password?: boolean;
}

export interface AuthContextType {
  user: UserProfile | null;
  tenant: string | null;
  roles: string[];
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (credentials: { email?: string; password?: string; rememberMe?: boolean }) => Promise<any>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  getProfile: () => Promise<UserProfile | null>;
  hasRole: (role: string | string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  getRoleRedirectPath: (role?: string) => string;
  setSession: (user: UserProfile, access: string, refresh?: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const getRoleRedirectPath = (role?: string, portal?: string): string => {
  const r = (role || "").toLowerCase();
  const p = (portal || "").toUpperCase();

  if (p === "INCOMPLETE_SETUP") {
    return "/register";
  }

  if (p === "COLLEGE_ADMIN" || p === "SCHOOL_ADMIN" || p === "PLATFORM_ADMIN") {
    return "/dashboard";
  }
  if (r === "super_admin" || r === "super admin" || r === "administrator" || r === "principal" || r === "dean" || r === "hod") {
    return "/dashboard";
  }
  if (r === "student" || p === "STUDENT") {
    return "/dashboard/student";
  }
  if (r === "faculty" || r === "teacher" || p === "STAFF") {
    return "/dashboard/teacher";
  }
  if (r === "parent" || p === "PARENT") {
    return "/dashboard/parent";
  }
  if (r === "accountant" || r === "finance") {
    return "/dashboard/accountant";
  }
  if (r === "librarian" || r === "library") {
    return "/dashboard/library";
  }
  if (r === "hostel_warden" || r === "warden") {
    return "/dashboard/hostel";
  }
  return "/dashboard";
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const roles = user?.roles || (user?.role ? [user.role] : []);
  const permissions = user?.permissions || [];
  const tenant = user?.tenant || null;
  const isAuthenticated = !!user && !!tokenStorage.getAccessToken();
  const mustChangePassword = !!user?.must_change_password;

  // Restore Session on Mount
  useEffect(() => {
    const initAuth = async () => {
      const token = tokenStorage.getAccessToken();
      if (token) {
        try {
          const profile = await authService.getProfile();
          setUser(profile);
        } catch (err) {
          tokenStorage.clearTokens();
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();

    // Listen for unauthorized events dispatched by axios interceptor
    const handleUnauthorized = () => {
      setUser(null);
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized);

    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, []);

  const login = async (credentials: { email?: string; password?: string; rememberMe?: boolean }) => {
    setIsLoading(true);
    try {
      const res = await authService.login(credentials);
      let profile: UserProfile;
      if (res.user) {
        profile = res.user;
      } else {
        profile = await authService.getProfile();
      }
      if (res.must_change_password !== undefined) {
        profile.must_change_password = res.must_change_password;
      }
      setUser(profile);
      return res;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  };

  const refreshToken = async () => {
    await authService.refreshToken();
    const profile = await authService.getProfile();
    setUser(profile);
  };

  const getProfile = async () => {
    const profile = await authService.getProfile();
    setUser(profile);
    return profile;
  };

  const hasRole = (roleReq: string | string[]) => {
    if (!user) return false;
    const required = Array.isArray(roleReq) ? roleReq : [roleReq];
    const userRoleList = roles.map((x) => x.toLowerCase());
    if (userRoleList.includes("super_admin") || userRoleList.includes("super admin")) return true;
    return required.some((r) => userRoleList.includes(r.toLowerCase()));
  };

  const hasPermission = (permissionReq: string) => {
    if (!user) return false;
    const userRoleList = roles.map((x) => x.toLowerCase());
    if (userRoleList.includes("super_admin") || userRoleList.includes("super admin")) return true;
    if (permissions.includes("all_permissions") || permissions.includes("*")) return true;
    return permissions.includes(permissionReq);
  };

  const setSession = (userProfile: UserProfile, access: string, refresh?: string) => {
    tokenStorage.setTokens(access, refresh || "");
    setUser(userProfile);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        roles,
        permissions,
        isAuthenticated,
        isLoading,
        mustChangePassword,
        login,
        logout,
        refreshToken,
        getProfile,
        hasRole,
        hasPermission,
        getRoleRedirectPath,
        setSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
